// M004-F0748 (WebUI #188, design section 8.1). The "Claims to review" tab of the Ledger page: the queue of one organisation by state
// (Submitted / Returned / Approved / All) as a listbox, each row opening the read-only claim in the side drawer. The caller (the landing
// page) has already offered the tab and the organisation list only for holders of ledger_manage; the Ledger enforces the same
// permission (ENVELOPE_CLAIM_REVIEW_DENIED is shown, never hidden). No private (unsubmitted) envelope is ever listed - the queue says so.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { claimReviewApi } from '../../services/claimReviewApi';
import ClaimReviewDrawer from './ClaimReviewDrawer';
import { STATE_TABS, formatDay, money, queueOf, refusalInfo, stateText, submittedCount } from './claimReviewModel';
import './ClaimsReviewTab.css';

export default function ClaimsReviewTab({ organisations = [], orgId, onSelectOrg, onSubmittedCount }) {
  const [stateKey, setStateKey] = useState('submitted');
  const [load, setLoad] = useState({ status: 'loading', claims: [], error: null });
  const [openId, setOpenId] = useState(null);
  const [active, setActive] = useState(0);
  const [toast, setToast] = useState('');
  const request = useRef(0);
  const listRef = useRef(null);
  const org = organisations.find((item) => String(item.id) === String(orgId));

  const refresh = useCallback(async () => {
    const mine = request.current + 1;
    request.current = mine;
    setLoad((current) => ({ status: 'loading', claims: current.claims, error: null }));
    try {
      const claims = queueOf(await claimReviewApi.queue(orgId, stateKey === 'all' ? undefined : stateKey));
      if (request.current !== mine) return;
      setLoad({ status: 'ready', claims, error: null });
      setActive(0);
      if (stateKey === 'submitted') onSubmittedCount?.(orgId, submittedCount(claims));
    } catch (cause) {
      if (request.current !== mine) return;
      setLoad({ status: 'error', claims: [], error: refusalInfo(cause) });
    }
  }, [orgId, stateKey, onSubmittedCount]);

  useEffect(() => { setOpenId(null); }, [orgId]);
  useEffect(() => { refresh(); }, [refresh]);

  const opened = load.claims.find((claim) => claim.transaction_id === openId) || null;
  const done = ({ kind, transactionId, name }) => {
    setOpenId(null);
    setToast(kind === 'return' ? `Returned "${name}" (${transactionId}). The claimant can see your reason.` : `Approved "${name}" (${transactionId}).`);
    refresh();
    // The count badge tracks the submitted queue whatever tab is showing.
    if (stateKey !== 'submitted') claimReviewApi.queue(orgId, 'submitted').then((response) => onSubmittedCount?.(orgId, submittedCount(queueOf(response)))).catch(() => {});
  };

  const onListKey = (event) => {
    const last = load.claims.length - 1;
    if (last < 0) return;
    let next = active;
    if (event.key === 'ArrowDown') next = Math.min(last, active + 1);
    else if (event.key === 'ArrowUp') next = Math.max(0, active - 1);
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = last;
    else if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); setOpenId(load.claims[active].transaction_id); return; }
    else return;
    event.preventDefault();
    setActive(next);
    listRef.current?.children[next]?.focus();
  };

  return (
    <section className="cr-tab" aria-labelledby="cr-tab-title" data-testid="claims-tab">
      <header className="cr-header">
        <div>
          <span className="cr-eyebrow">Ledger</span>
          <h2 id="cr-tab-title">Claims to review</h2>
          <p className="cr-muted">Expense claims submitted to {org?.name || `organisation ${orgId}`}. Each claim is approved or returned as a whole.</p>
        </div>
        {organisations.length > 1 ? (
          <label className="cr-org">Organisation
            <select value={String(orgId)} onChange={(event) => onSelectOrg?.(event.target.value)}>
              {organisations.map((item) => <option key={item.id} value={String(item.id)}>{item.name}</option>)}
            </select>
          </label>
        ) : null}
      </header>

      <div className="cr-states" role="group" aria-label="Claim state">
        {STATE_TABS.map((tab) => (
          <button key={tab.key} type="button" className={`cr-state-btn${stateKey === tab.key ? ' active' : ''}`} aria-pressed={stateKey === tab.key} data-testid={`claims-state-${tab.key}`} onClick={() => setStateKey(tab.key)}>
            {tab.label}
          </button>
        ))}
      </div>

      <p className="cr-toast" role="status" data-testid="claims-toast">{toast}</p>

      {load.status === 'loading' && load.claims.length === 0 ? (
        <div className="cr-skel" role="status" aria-busy="true" aria-label="Loading claims" data-testid="claims-skeleton">{[0, 1, 2].map((n) => <div key={n} className="cr-skel-row" aria-hidden="true" />)}</div>
      ) : null}
      {load.status === 'error' ? (
        <div className="cr-error" role="alert" data-testid="claims-error">
          <strong>The claims could not be loaded.</strong> <span>{load.error.message}</span> {load.error.code ? <code>{load.error.code}</code> : null}
          <button type="button" className="cr-btn" onClick={refresh}>Try again</button>
        </div>
      ) : null}
      {load.status === 'ready' && load.claims.length === 0 ? (
        <p className="cr-empty" data-testid="claims-empty">{stateKey === 'all' ? 'No claims have been submitted to this organisation.' : `No ${stateText(stateKey).toLowerCase()} claims.`}</p>
      ) : null}

      {load.claims.length ? (
        <ul ref={listRef} className="cr-list" role="listbox" aria-label="Claims" data-testid="claims-list" onKeyDown={onListKey}>
          {load.claims.map((claim, index) => (
            <li
              key={claim.transaction_id}
              role="option"
              aria-selected={openId === claim.transaction_id}
              tabIndex={index === active ? 0 : -1}
              className="cr-row-item"
              data-testid={`claim-row-${claim.transaction_id}`}
              data-own={claim.own_claim ? 'true' : 'false'}
              onClick={() => { setActive(index); setOpenId(claim.transaction_id); }}
              onFocus={() => setActive(index)}
            >
              <span className="cr-who">{claim.claimant_label}{claim.own_claim ? <em className="cr-own" data-testid="claim-own-flag"> (your claim)</em> : null}</span>
              <span className="cr-name">{claim.envelope_name}</span>
              <span className="cr-meta">{claim.receipt_count} {claim.receipt_count === 1 ? 'receipt' : 'receipts'}</span>
              <span className="cr-amt">{money(claim.total_minor, claim.currency)}</span>
              <span className="cr-meta">{formatDay(claim.submitted_at)}</span>
              <span className={`cr-pill cr-state-${claim.state}`}>{stateText(claim.state)}</span>
            </li>
          ))}
        </ul>
      ) : null}

      {opened ? <ClaimReviewDrawer orgId={orgId} summary={opened} onClose={() => setOpenId(null)} onDone={done} /> : null}
    </section>
  );
}
